import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { Channel, DefaultGenerics } from 'stream-chat'
import { markReadWithRetry } from '../services/ChatApiService'
import { ChatClientContextV2 } from './ChatClientProviderV2'

export interface ChatActiveChannelContextV2Value {
  activeChannelId: string | null
  activeChannel: Channel<DefaultGenerics> | null
  isLoading: boolean
  setActiveChannelId: (channelId: string | null) => void
  expandedCases: Record<string, boolean>
  setExpandedCases: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >
  currentCaseChatId: string | null
  setCurrentCaseChatId: (caseChatId: string | null) => void
}

const defaultValue: ChatActiveChannelContextV2Value = {
  activeChannelId: null,
  activeChannel: null,
  isLoading: false,
  setActiveChannelId: () => {},
  expandedCases: {},
  setExpandedCases: () => {},
  currentCaseChatId: null,
  setCurrentCaseChatId: () => {},
}

export const ChatActiveChannelContextV2 =
  createContext<ChatActiveChannelContextV2Value>(defaultValue)

export const ChatActiveChannelProviderV2: React.FC<{
  children: ReactNode
}> = ({ children }) => {
  const { client, currentUserId } = useContext(ChatClientContextV2)
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null)
  const [activeChannel, setActiveChannel] =
    useState<Channel<DefaultGenerics> | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [expandedCases, setExpandedCases] = useState<Record<string, boolean>>(
    {}
  )
  const [currentCaseChatId, setCurrentCaseChatId] = useState<string | null>(
    null
  )

  // Bug fix #4: call markRead when opening a channel
  const resolveAndMarkRead = useCallback(
    async (channelId: string) => {
      if (!client || !currentUserId) return

      setIsLoading(true)
      try {
        // Try to resolve from already-watched channels before querying
        const existingCid = `messaging:${channelId}`
        let channel = client.activeChannels[existingCid] ?? null

        if (!channel) {
          const channels = await client.queryChannels({
            id: channelId,
            members: { $in: [currentUserId] },
          })
          channel = channels[0] ?? null
        }

        setActiveChannel(channel)

        if (channel) {
          // markRead triggers notification.mark_read event which the
          // unread provider listens to — no need to refresh explicitly
          await markReadWithRetry(channel)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('ChatV2: Failed to resolve channel:', error)
        setActiveChannel(null)
      } finally {
        setIsLoading(false)
      }
    },
    [client, currentUserId]
  )

  useEffect(() => {
    if (activeChannelId) {
      resolveAndMarkRead(activeChannelId)
    } else {
      setActiveChannel(null)
    }
  }, [activeChannelId, resolveAndMarkRead])

  const value = useMemo(
    () => ({
      activeChannelId,
      activeChannel,
      isLoading,
      setActiveChannelId,
      expandedCases,
      setExpandedCases,
      currentCaseChatId,
      setCurrentCaseChatId,
    }),
    [
      activeChannelId,
      activeChannel,
      isLoading,
      expandedCases,
      currentCaseChatId,
    ]
  )

  return (
    <ChatActiveChannelContextV2.Provider value={value}>
      {children}
    </ChatActiveChannelContextV2.Provider>
  )
}
